import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import EDButton from '../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';
import EkonomiskAnalysSaveAnalysModal, {
  AnalysDistribution,
} from './Components/EkonomiskAnalysSaveAnalysModal/EkonomiskAnalysSaveAnalysModal';
import { ILocalAnalys } from '../../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
  ISelectedAnalys,
  IGrundurval,
} from '../../../../EkonomiskAnalysPage';
import {
  SaveAnalysFnType,
  SaveAnalysCallbackType,
} from './Mutations/SaveAnalysMutation/SaveAnalysMutation';
import { localAnalysToTedEkAnalys } from '../../../../Utils/convertUtils';
import { SaveFavoriteAnalysFnType } from '../../EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/Queries/ChangeAnalysFavoriteMutation/ChangeAnalysFavoriteMutation';
import { useToggle } from '../../../../../../../../../Hooks/booleanHooks';
import EkonomiskAnalysDeleteAnalysConfirmation from './Components/EkonomiskAnalysDeleteAnalysConfirmation/EkonomiskAnalysDeleteAnalysConfirmation';
import { SaveAnalysVariables } from './Mutations/SaveAnalysMutation/_QueryTypes/SaveAnalys';
import EDGrid from '../../../../../../../../../BaseComponents/GridComponents/EDGrid';
import EDIconButton from '../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { IUserInfo } from '../../../../Utils/ekonomiskAnalysInitQueryUtils';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import DialogScrollOverlay from '../../../../../../../../../Components/DialogScrollOverlay/DialogScrollOverlay';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

interface IProps {
  sideMenuOpen: boolean;
  openSideMenu: () => void;
  settingsChanged: boolean;
  onAnalysSelected: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean
  ) => void;
  analysSettings: ILocalAnalys;
  selectedAnalys: ISelectedAnalys;
  dbName: string;
  saveAnalys: SaveAnalysFnType;
  changeFavoriteAnalys: SaveFavoriteAnalysFnType;
  deleteAnalys: () => void;
  updateReport: (updateOrig?: boolean) => void;
  selectedAnalysNewlyCreated: boolean;
  userInfo?: IUserInfo;
  disabled?: boolean;
  className?: string;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  sammanfattningsMode?: boolean;
}

enum SaveFunction {
  Save,
  SaveAs,
  Edit,
  Delete,
}

const styles = (theme: Theme) =>
  ({
    container: {
      padding: '8px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      zIndex: 0,
    },
    containerSideMenuOpen: {
      backgroundColor:
        theme.customProperties.EASideMenuColors.selectedOptionsSectionBGColor,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: '8px 0',
      marginLeft: '-2px',
    },
    saveMenuIcon: {
      margin: '-6px 0px 0px -4px',
    },
    saveMenuGridItem: {
      height: 52,
    },
    loadingProgress: {
      background: 'inherit',
      overflow: 'hidden',
      width: 'auto',
      marginRight: 4,
    },
  } as const);

const toggleFromList = <T extends {}>(prevList: T[], toToggle: T): T[] => {
  const index = prevList.indexOf(toToggle);
  if (index === -1) {
    return [...prevList, toToggle];
  }

  return prevList.filter(x => x !== toToggle);
};

/**
 * En toolbar för att spara ekonomiska analyser. TestAnalys är satt som en existerande analys
 */
export const EkonomiskAnalysSaveAnalysView: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [openModal, toggleOpenModal] = useToggle();
  const [openConfirmation, toggleOpenConfirmation] = useToggle();
  const [isNewAnalys, setIsNewAnalys] = useState(true);
  const [loadingFunctions, setLoadingFunctions] = useState<SaveFunction[]>([]);
  const sideMenuContext = useContext(EkonomiskAnalysSideMenuContext);

  const { other } = props.analysSettings;

  const overwriteAnalys = () => {
    const { analys } = props.selectedAnalys;
    setLoadingFunctions(prevFunctions =>
      toggleFromList(prevFunctions, SaveFunction.Save)
    );
    props.saveAnalys(
      {
        analys: localAnalysToTedEkAnalys(props.analysSettings, analys.id),
        dbname: props.dbName,
        new_analys: false,
        anv_urval: tryConvertUIUrvalToTEDUrval(props.analysSettings.urval),
        options: {
          antal_decimaler: other.decimals,
          endast_bokfort: !other.includeEjBokfordaJournaler,
          enhet: String(other.denomination),
          visa_jamforelse: other.showComparison,
          summerings_mode: other.summationMode.value,
          exkludera_personalkostnader: other.exkluderaPersonalKostnader,
          visa_summering: props.analysSettings.visaSummeringar,
          expand_to_index: sideMenuContext.expandedSumLevel,
        },
        grundurval_id: props.selectedAnalys.analys.grundurval
          ? props.selectedAnalys.analys.grundurval.id
          : undefined,
      },
      props.selectedAnalys,
      props.selectedAnalys.analys.grundurval,
      props.userInfo,
      () => {
        setLoadingFunctions(prevFunctions =>
          toggleFromList(prevFunctions, SaveFunction.Save)
        );
        props.updateReport(true);
      }
    );
  };

  const saveAnalys = (
    mutateOptions: SaveAnalysVariables,
    callback?: SaveAnalysCallbackType
  ) => {
    setLoadingFunctions(prev => toggleFromList(prev, SaveFunction.SaveAs));
    props.saveAnalys(
      {
        ...mutateOptions,
        options: {
          ...mutateOptions.options,
          expand_to_index: sideMenuContext.expandedSumLevel,
        },
      },
      { ...props.selectedAnalys },
      props.selectedAnalys.analys.grundurval,
      props.userInfo,
      (
        id: string,
        analysName: string,
        utdelningLopnr: number | null,
        grundurval: IGrundurval | undefined
      ) => {
        toggleOpenModal();

        setLoadingFunctions(prevFunctions =>
          toggleFromList(prevFunctions, SaveFunction.SaveAs)
        );

        if (callback) {
          callback(id, analysName, utdelningLopnr, grundurval);
        }
      }
    );
  };

  const lokalGemensamAnalys = () => {
    return (
      props.analysSettings.category != null &&
      props.analysSettings.category === 'Gemensamma lokala'
    );
  };

  const notEgenAnalys =
    !Object.values(AnalysDistribution).includes(
      props.analysSettings.category as AnalysDistribution
    ) || lokalGemensamAnalys();

  const canSaveGemensamma = props.userInfo?.isUtdelningBehorig ?? false;

  const egenAnalys = !notEgenAnalys;

  const canSave = () => {
    if (radbaserad) {
      return false;
    } else if (egenAnalys) {
      return true;
    } else {
      return canSaveGemensamma && lokalGemensamAnalys();
    }
  };

  const saveDisabledReason = () => {
    if (radbaserad) {
      return 'Kan inte spara radbaserad analys';
    } else if (egenAnalys) {
      return !props.settingsChanged ? 'Analysen är inte ändrad' : '';
    } else if (!lokalGemensamAnalys()) {
      return 'Kan inte spara över standardanalyser';
    } else if (!canSaveGemensamma) {
      return 'Ej behörig för att spara över lokala gemensamma analyser';
    } else {
      return '';
    }
  };

  const canEdit = () => {
    return canSave() && !props.analysSettings.sparadForAnnanAnvandare;
  };

  const editDisabledReason = () => {
    if (props.analysSettings.sparadForAnnanAnvandare) {
      return 'Kan inte redigera annan användares analys';
    } else if (radbaserad) {
      return 'Kan inte redigera radbaserad analys';
    } else if (!lokalGemensamAnalys()) {
      return 'Kan inte redigera standardanalyser';
    } else if (!canSaveGemensamma) {
      return 'Ej behörig för att redigera lokala gemensamma analyser';
    } else {
      return '';
    }
  };

  const canDelete = () => {
    return canEdit();
  };

  const deleteDisabledReason = () => {
    if (props.analysSettings.sparadForAnnanAnvandare) {
      return 'Kan inte ta bort annan användares analys';
    } else if (radbaserad) {
      return 'Kan ej ta bort radbaserad analys';
    } else if (notEgenAnalys && !lokalGemensamAnalys()) {
      return 'Kan inte ta bort standardanalyser';
    } else if (notEgenAnalys && !canSaveGemensamma) {
      return 'Ej behörig för att ta bort lokala gemensamma analyser';
    } else {
      return '';
    }
  };

  const saveAsDisabledReason = (
    radbaserad: boolean,
    disabled: boolean | undefined,
    sammanfattningsMode: boolean | undefined
  ) => {
    if (radbaserad || disabled) {
      return 'Kan inte spara radbaserad analys';
    } else if (sammanfattningsMode) {
      return 'Kan inte spara en detaljanalys';
    } else {
      return '';
    }
  };

  const openConfirmationModal = (newAnalys: boolean) => {
    setIsNewAnalys(newAnalys);
    toggleOpenModal();
  };

  const deleteAnalys = () => {
    toggleOpenConfirmation();
    props.deleteAnalys();
  };

  const radbaserad = props.analysSettings.type === 'RAPPORTDIMENSIONRADDRIVEN';
  const classes = withStyles.getClasses(props);
  return (
    <>
      <DialogScrollOverlay
        disableShadow={!props.sideMenuOpen}
        classes={{
          container: classNames(props.className, classes.container, {
            [classes.containerSideMenuOpen]: props.sideMenuOpen,
          }),
        }}
      >
        <EDGrid container spacing={0}>
          <EDGrid item xs={3} className={classes.saveMenuGridItem}>
            {!props.sideMenuOpen && (
              <EDIconButton
                tooltip="Spara/Redigera"
                tooltipPlacement="right"
                onClick={props.openSideMenu}
                className={classes.saveMenuIcon}
                icon="save_alt"
                disabled={radbaserad || props.disabled}
                disabledReason={
                  radbaserad
                    ? 'Kan ej spara eller redigera radbaserad analys'
                    : undefined
                }
              />
            )}
            {props.sideMenuOpen && (
              <EDButton
                onClick={overwriteAnalys}
                icon={
                  loadingFunctions.includes(SaveFunction.Save) ? (
                    <EDAvenyProgress />
                  ) : (
                    <EDIcon iconType="save_alt" color="inherit" />
                  )
                }
                label="Spara"
                captionLabel
                disabled={
                  !canSave() || !props.settingsChanged || props.disabled
                }
                disabledReason={saveDisabledReason()}
                color="primary"
                labelNotUpperCase
                fullWidth
                flat
              />
            )}
          </EDGrid>
          <EDGrid item xs={3} className={classes.saveMenuGridItem}>
            {props.sideMenuOpen && (
              <EDButton
                onClick={() => openConfirmationModal(true)}
                icon={<EDIcon iconType="save_as" color="inherit" />}
                label="Spara som"
                captionLabel
                color="primary"
                disabled={
                  radbaserad || props.disabled || props.sammanfattningsMode
                }
                disabledReason={saveAsDisabledReason(
                  radbaserad,
                  props.disabled,
                  props.sammanfattningsMode
                )}
                labelNotUpperCase
                fullWidth
                flat
              />
            )}
          </EDGrid>
          <EDGrid item xs={3} className={classes.saveMenuGridItem}>
            {props.sideMenuOpen && (
              <EDButton
                onClick={() => openConfirmationModal(false)}
                icon={<EDIcon iconType="edit" color="inherit" />}
                label="Redigera"
                captionLabel
                disabled={!canEdit() || props.disabled}
                disabledReason={editDisabledReason()}
                color="primary"
                labelNotUpperCase
                fullWidth
                flat
              />
            )}
          </EDGrid>
          <EDGrid item xs={3} className={classes.saveMenuGridItem}>
            {props.sideMenuOpen && (
              <EDButton
                onClick={toggleOpenConfirmation}
                icon={
                  loadingFunctions.includes(SaveFunction.Delete) ? (
                    <EDAvenyProgress />
                  ) : (
                    <EDIcon iconType="delete" color="inherit" />
                  )
                }
                label="Ta bort"
                captionLabel
                disabled={!canDelete() || props.disabled}
                disabledReason={deleteDisabledReason()}
                color="primary"
                labelNotUpperCase
                fullWidth
                flat
              />
            )}
          </EDGrid>
        </EDGrid>
      </DialogScrollOverlay>
      {openModal && (
        <EkonomiskAnalysSaveAnalysModal
          open={openModal}
          toggleOpen={toggleOpenModal}
          analysSettings={props.analysSettings}
          selectedAnalys={props.selectedAnalys}
          dbName={props.dbName}
          saveAnalys={saveAnalys}
          changeFavoriteAnalys={props.changeFavoriteAnalys}
          selectAnalys={props.onAnalysSelected}
          saving={loadingFunctions.includes(SaveFunction.SaveAs)}
          newAnalys={isNewAnalys}
          toggleSaving={() =>
            setLoadingFunctions(prev =>
              toggleFromList(prev, SaveFunction.SaveAs)
            )
          }
          userInfo={props.userInfo}
        />
      )}
      <EkonomiskAnalysDeleteAnalysConfirmation
        benamning={props.selectedAnalys.analys.name}
        open={openConfirmation}
        toggleOpen={toggleOpenConfirmation}
        deleteAnalys={deleteAnalys}
        lokalGemensam={lokalGemensamAnalys()}
      />
    </>
  );
};

export default withStyles(EkonomiskAnalysSaveAnalysView, styles);
